import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Alert,
  Paper,
  Avatar,
  Grid,
  Button,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  NavigateNext as NavigateNextIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { ShadowBox } from "../../../../components/styles/styledComponent";
import ClickableChip from "../../../../components/common/ClickableChip";
import { getEmployees, regenerateDocumentUploadToken } from "../../../../api/employeeApi";
import { getStatusColor } from "../../../../utils/helper/tablehelper";
import CustomNoResultsOverlay from "./components/CustomNoResultsOverlay";

const filterInputStyle = {
  borderRadius: "10px",
  backgroundColor: "#fff",
  transition: "all 0.2s ease-in-out",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(226, 232, 240, 0.8)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(117, 34, 209, 0.4)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#7522D1",
  },
};

const stringToColor = (string) => {
  if (!string) return "#7522D1";
  let hash = 0;
  for (let i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = "#";
  for (let i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
};

const ExpiredJoiningDirectory = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });

  const fetchEmployeesData = async () => {
    try {
      setLoading(true);
      // Hardcoded filter for expired joining
      const filters = { expiredJoining: "true" };
      
      const response = await getEmployees(filters);
      if (response && response.success) {
        const rows = response.data.rows || response.data || [];
        const formattedData = rows.map((emp) => ({
          ...emp,
          id: emp.id,
          empNo: emp.employeeId || "N/A",
          name: `${emp.firstName || ""} ${emp.lastName || ""}`.trim() || "N/A",
          designation: emp.designation?.name || "N/A",
          department: emp.department?.name || "N/A",
          expectedJoiningDate: emp.expectedJoiningDate ? emp.expectedJoiningDate.split('T')[0] : "N/A",
          status: (emp.status || "PENDING").toUpperCase(),
        }));
        setEmployees(formattedData);
      }
    } catch (error) {
      console.error("Error fetching expired joining data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployeesData();
  }, []);

  const handleMenuOpen = (event, employee) => {
    setAnchorEl(event.currentTarget);
    setSelectedEmployee(employee);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedEmployee(null);
  };

  const handleResendLink = async () => {
    if (!selectedEmployee) return;
    try {
      handleMenuClose();
      setLoading(true);
      const result = await regenerateDocumentUploadToken(selectedEmployee.id);
      if (result.success) {
        setSnackbar({
          open: true,
          message: `Onboarding link sent to ${selectedEmployee.email}`,
          severity: "success",
        });
      } else {
        setSnackbar({
          open: true,
          message: result.message || "Failed to resend link",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({ open: true, message: "Error resending link", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    return employees.filter((emp) =>
      emp.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.empNo.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.email.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [employees, searchQuery]);

  const columns = [
    {
      field: "empNo",
      headerName: "EMPLOYEE NO.",
      width: 140,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            variant="body2"
            sx={{ color: "#7522D1", fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}
            onClick={() => navigate(`/my-team/employee-directory/${params.row.id}`)}
          >
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "name",
      headerName: "EMPLOYEE DETAILS",
      width: 250,
      renderCell: (params) => {
        const avatarColor = stringToColor(params.row.name || params.row.email || "EE");
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, height: "100%", minWidth: 0 }}>
            <Avatar
              sx={{
                bgcolor: avatarColor,
                color: "#fff",
                width: 36,
                height: 36,
                fontSize: "0.95rem",
                fontWeight: 700,
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              }}
            >
              {params.row.firstName?.[0] || ""}
              {params.row.lastName?.[0] || ""}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                noWrap
                sx={{ fontWeight: 600, color: "#1e293b", lineHeight: 1.2 }}
              >
                {params.row.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                {params.row.email || "No email"}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    { field: "department", headerName: "DEPARTMENT", width: 180 },
    { field: "designation", headerName: "DESIGNATION", width: 180 },
    { 
      field: "expectedJoiningDate", 
      headerName: "EXPECTED JOINING", 
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography variant="body2" sx={{ color: "error.main", fontWeight: 600 }}>
            {params.value}
          </Typography>
        </Box>
      )
    },
    {
      field: "status",
      headerName: "STATUS",
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <ClickableChip status={params.value} />
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "ACTIONS",
      width: 100,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row)} sx={{ color: "#64748b" }}>
            <MoreVertIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      {/* Breadcrumbs */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" sx={{ color: "#94a3b8" }} />}
          sx={{
            mb: 1.5,
            "& .MuiBreadcrumbs-li": {
              fontSize: "0.875rem",
            },
          }}
        >
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate("/dashboard")}
            sx={{
              cursor: "pointer",
              color: "#4F4937",
              fontWeight: 400,
              fontSize: "0.875rem",
              "&:hover": { color: "#7522D1" },
            }}
          >
            Dashboard
          </Link>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate("/my-team")}
            sx={{
              cursor: "pointer",
              color: "#4F4937",
              fontWeight: 400,
              fontSize: "0.875rem",
              "&:hover": { color: "#7522D1" },
            }}
          >
            My Team
          </Link>
          <Typography
            color="text.primary"
            sx={{
              color: "#7522D1",
              fontWeight: 600,
              fontSize: "0.875rem",
            }}
          >
            Expired Joining
          </Typography>
        </Breadcrumbs>

        {/* Premium Warning Header Card */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: "16px",
            background: "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(226, 232, 240, 0.8)",
            borderLeft: "6px solid #EF4444",
            boxShadow: "0 4px 20px rgba(239, 68, 68, 0.02)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1, minWidth: "280px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <WarningIcon sx={{ color: "#EF4444", fontSize: "2rem" }} />
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: "#030007",
                  letterSpacing: "-0.02em",
                }}
              >
                Expired Joining Date Directory
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: "#4F4937", mt: 1, pl: 0.5, fontSize: "0.95rem" }}>
              List of prospective employees whose expected joining dates have passed but whose onboarding process remains incomplete. Please follow up or regenerate invitation links.
            </Typography>
          </Box>
        </Paper>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 0,
          overflow: "hidden",
          mb: 4,
          borderRadius: "16px",
          border: "1px solid rgba(117, 34, 209, 0.08)",
          boxShadow: "0 8px 32px rgba(148, 163, 184, 0.04)",
          backgroundColor: "#ffffff",
        }}
      >
        <Box
          sx={{
            p: 2.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
            bgcolor: "rgba(255, 255, 255, 0.75)",
            backdropFilter: "blur(12px)",
          }}
        >
          <TextField
            size="small"
            placeholder="Search by ID, Name, Email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", md: 320 },
              "& .MuiOutlinedInput-root": filterInputStyle,
            }}
          />
          <Button
            variant="outlined"
            onClick={fetchEmployeesData}
            startIcon={<RefreshIcon />}
            sx={{
              borderColor: "#7522D1",
              color: "#7522D1",
              textTransform: "none",
              borderRadius: "10px",
              fontWeight: 600,
              bgcolor: "#ffffff",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                borderColor: "#5e1ba8",
                backgroundColor: "rgba(117, 34, 209, 0.04)",
                transform: "translateY(-2px)",
              },
              "&:active": {
                transform: "translateY(0)",
              },
            }}
          >
            Sync Data
          </Button>
        </Box>

        <Box sx={{ height: 600, width: "100%", bgcolor: "#ffffff" }}>
          <DataGrid
            rows={filteredData}
            columns={columns}
            loading={loading}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            disableSelectionOnClick
            rowHeight={64}
            slots={{ noRowsOverlay: CustomNoResultsOverlay }}
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#F8F5FC",
                borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
              },
              "& .MuiDataGrid-columnHeader": {
                color: "#42374F",
                fontWeight: 700,
                textTransform: "uppercase",
                fontSize: "0.75rem",
                letterSpacing: "0.05em",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid rgba(241, 245, 249, 0.8)",
              },
              "& .MuiDataGrid-row": {
                transition: "background-color 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(117, 34, 209, 0.015)",
                },
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "1px solid rgba(226, 232, 240, 0.8)",
                backgroundColor: "#fff",
              },
              "& ::-webkit-scrollbar": {
                width: "8px",
                height: "8px",
              },
              "& ::-webkit-scrollbar-track": {
                background: "#f8fafc",
              },
              "& ::-webkit-scrollbar-thumb": {
                background: "#ddd",
                borderRadius: "4px",
              },
              "& ::-webkit-scrollbar-thumb:hover": {
                background: "#ccc",
              },
            }}
          />
        </Box>
      </Paper>

      {/* Action Popover Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 10px 30px rgba(117, 34, 209, 0.08)",
            border: "1px solid rgba(117, 34, 209, 0.08)",
            p: 0.5,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            navigate(`/my-team/employee-directory/${selectedEmployee?.id}`);
            handleMenuClose();
          }}
          sx={{
            borderRadius: "8px",
            py: 1.2,
            px: 2,
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(117, 34, 209, 0.04)",
              color: "#7522D1",
              "& .MuiListItemIcon-root": { color: "#7522D1" },
            },
          }}
        >
          <ListItemIcon sx={{ color: "#7522D1", minWidth: "36px" }}>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="View Details"
            primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            navigate(`/my-team/add-employee/${selectedEmployee?.id}/basic-details`);
            handleMenuClose();
          }}
          sx={{
            borderRadius: "8px",
            py: 1.2,
            px: 2,
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(117, 34, 209, 0.04)",
              color: "#7522D1",
              "& .MuiListItemIcon-root": { color: "#7522D1" },
            },
          }}
        >
          <ListItemIcon sx={{ color: "#7522D1", minWidth: "36px" }}>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Edit Employee"
            primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
          />
        </MenuItem>
        <MenuItem
          onClick={handleResendLink}
          sx={{
            borderRadius: "8px",
            py: 1.2,
            px: 2,
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(117, 34, 209, 0.04)",
              color: "#7522D1",
              "& .MuiListItemIcon-root": { color: "#7522D1" },
            },
          }}
        >
          <ListItemIcon sx={{ color: "#f97316", minWidth: "36px" }}>
            <RefreshIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Resend Onboarding Link"
            primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
          />
        </MenuItem>
      </Menu>

      {/* Snackbar Alert */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: "12px",
            fontWeight: 600,
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
            "&.MuiAlert-filledSuccess": {
              backgroundColor: "#7522D1",
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ExpiredJoiningDirectory;
